import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Plus, Trash2, Edit2, RefreshCw, Eye, EyeOff,
  Sparkles, Save, ExternalLink, Globe, Send, ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const PAGE_TYPES = [
  { value: 'about', label: 'Sobre Nosotros', slug: 'sobre-nosotros' },
  { value: 'privacy', label: 'Política de Privacidad', slug: 'privacidad' },
  { value: 'terms', label: 'Términos y Condiciones', slug: 'terminos' },
  { value: 'contact', label: 'Contacto', slug: 'contacto' },
  { value: 'faq', label: 'Preguntas Frecuentes', slug: 'faq' },
  { value: 'general', label: 'Página General', slug: '' },
];

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const fetchPages = useCallback(async () => {
    try {
      const data = await api.getStaticPages();
      setPages(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchPages();
  }, [fetchPages]);
  
  const handleDelete = async (slug) => {
    if (!window.confirm('¿Eliminar esta página? Esta acción no se puede deshacer.')) return;
    try {
      await api.deleteStaticPage(slug);
      fetchPages();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleTogglePublish = async (page) => {
    try {
      if (page.is_published) {
        await api.unpublishStaticPage(page.slug);
      } else {
        await api.publishStaticPage(page.slug);
      }
      fetchPages();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleSave = async (pageData) => {
    try {
      if (editingPage) {
        await api.updateStaticPage(editingPage.slug, pageData);
      } else {
        await api.createStaticPage(pageData);
      }
      setEditingPage(null);
      setShowCreateModal(false);
      fetchPages();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            {pages.length} páginas - Gestiona el contenido de las páginas estáticas del sitio
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPages}>
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button size="sm" onClick={() => setShowCreateModal(true)} className="bg-gray-900">
            <Plus className="w-4 h-4 mr-1" /> Nueva Página
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No hay páginas creadas</p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Crear primera página
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map((page) => (
            <div key={page.slug} className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <Badge className={page.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {page.is_published ? 'Publicada' : 'Borrador'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleTogglePublish(page)}
                      title={page.is_published ? 'Despublicar' : 'Publicar'}
                    >
                      {page.is_published ? (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Eye className="w-4 h-4 text-green-600" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingPage(page)}>
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(page.slug)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-lg mb-1 line-clamp-1">{page.title}</h3>
                <p className="text-sm text-gray-400 mb-3 flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  /{page.slug}
                </p>
                
                {page.meta_description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{page.meta_description}</p>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-xs text-gray-400">
                    {page.content ? `${Math.ceil(page.content.length / 500)} min lectura` : 'Sin contenido'}
                  </span>
                  {page.is_published && (
                    <a 
                      href={`/page/${page.slug}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      Ver <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Create/Edit Modal */}
      <PageEditorModal
        open={showCreateModal || !!editingPage}
        onClose={() => { setShowCreateModal(false); setEditingPage(null); }}
        page={editingPage}
        onSave={handleSave}
      />
    </div>
  );
};

// ======================= PAGE EDITOR MODAL =======================
const PageEditorModal = ({ open, onClose, page, onSave }) => {
  const [form, setForm] = useState({
    slug: '',
    title: '',
    content: '',
    meta_description: '',
    meta_keywords: '',
    is_published: false
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState('general');
  const [aiInstruction, setAiInstruction] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  
  useEffect(() => {
    if (page) {
      setForm({ ...page });
      // Detect page type from slug
      const type = PAGE_TYPES.find(t => t.slug === page.slug);
      setSelectedType(type?.value || 'general');
    } else {
      setForm({
        slug: '',
        title: '',
        content: '',
        meta_description: '',
        meta_keywords: '',
        is_published: false
      });
      setSelectedType('general');
    }
    setAiInstruction('');
    setShowAiPanel(false);
    setActiveTab('content');
  }, [page, open]);
  
  const handleTypeChange = (type) => {
    setSelectedType(type);
    const typeInfo = PAGE_TYPES.find(t => t.value === type);
    if (typeInfo && typeInfo.slug && !page) {
      setForm(prev => ({
        ...prev,
        slug: typeInfo.slug,
        title: typeInfo.label
      }));
    }
  };
  
  const handleGenerateContent = async () => {
    setGenerating(true);
    try {
      const res = await api.generatePageContent({
        page_type: selectedType,
        instruction: aiInstruction,
        company_name: 'Influencia & Poder',
        current_content: form.content
      });
      
      if (res.success && res.content) {
        setForm(prev => ({ ...prev, content: res.content }));
        setShowAiPanel(false);
        setAiInstruction('');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title) {
      alert('Slug y título son requeridos');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {page ? 'Editar Página' : 'Nueva Página'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          {/* Tabs */}
          <div className="flex border-b mb-4">
            {['content', 'seo'].map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab 
                    ? 'border-gray-900 text-gray-900' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'content' ? 'Contenido' : 'SEO'}
              </button>
            ))}
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {activeTab === 'content' && (
              <>
                {/* Page Type & Basic Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo de Página</label>
                    <select
                      value={selectedType}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      disabled={!!page}
                    >
                      {PAGE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Slug (URL) *</label>
                    <div className="flex items-center">
                      <span className="text-gray-400 text-sm mr-1">/page/</span>
                      <Input
                        value={form.slug}
                        onChange={(e) => setForm({...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                        placeholder="mi-pagina"
                        disabled={!!page}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Título *</label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({...form, title: e.target.value})}
                      placeholder="Título de la página"
                      required
                    />
                  </div>
                </div>
                
                {/* AI Generation Panel */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-900">Generar Contenido con IA</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAiPanel(!showAiPanel)}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${showAiPanel ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                  
                  {showAiPanel && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-purple-700 block mb-1">
                          Instrucciones para la IA (opcional)
                        </label>
                        <Textarea
                          value={aiInstruction}
                          onChange={(e) => setAiInstruction(e.target.value)}
                          placeholder="Ej: Incluir información sobre el equipo editorial, mencionar que somos un medio independiente..."
                          rows={2}
                          className="bg-white"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-purple-600">
                          La IA generará contenido apropiado para "{PAGE_TYPES.find(t => t.value === selectedType)?.label}"
                        </p>
                        <Button
                          type="button"
                          onClick={handleGenerateContent}
                          disabled={generating}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {generating ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                          )}
                          {generating ? 'Generando...' : 'Generar Contenido'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!showAiPanel && (
                    <p className="text-sm text-purple-600">
                      Haz clic para expandir y generar contenido automáticamente
                    </p>
                  )}
                </div>
                
                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Contenido (HTML)
                    <span className="text-gray-400 font-normal ml-2">
                      {form.content.length} caracteres
                    </span>
                  </label>
                  <Textarea
                    value={form.content}
                    onChange={(e) => setForm({...form, content: e.target.value})}
                    placeholder="<h2>Título de sección</h2>
<p>Contenido del párrafo...</p>"
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>
                
                {/* Preview */}
                {form.content && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Vista Previa</label>
                    <div 
                      className="bg-white border rounded-lg p-4 max-h-60 overflow-y-auto prose prose-sm"
                      dangerouslySetInnerHTML={{ __html: form.content }}
                    />
                  </div>
                )}
              </>
            )}
            
            {activeTab === 'seo' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Meta Descripción</label>
                  <Textarea
                    value={form.meta_description}
                    onChange={(e) => setForm({...form, meta_description: e.target.value})}
                    placeholder="Descripción para motores de búsqueda (150-160 caracteres)"
                    rows={2}
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-400 mt-1">{form.meta_description.length}/160 caracteres</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Palabras Clave (Keywords)</label>
                  <Input
                    value={form.meta_keywords}
                    onChange={(e) => setForm({...form, meta_keywords: e.target.value})}
                    placeholder="política, economía, noticias, latinoamérica"
                  />
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={form.is_published}
                    onChange={(e) => setForm({...form, is_published: e.target.checked})}
                    className="rounded"
                  />
                  <label htmlFor="is_published" className="text-sm">
                    Publicar página inmediatamente
                  </label>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-gray-900">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Guardando...' : 'Guardar Página'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PagesManager;
